import {Localizer} from "../../../infrastructure/localization/localizer";
import { PdfReadySignal } from "./components/pdfReadySignal";
import {useEffect, useState} from "react";
import {useRouteMatch} from "react-router-dom";
import PdfComponent from "core/components/pdf/components/pdfComponent";
import {useFetchWithErrorHandlingPdf} from "./hooks/useFetchWithErrorHandlingPdf";
import useLogbogApi from "../../hooks/useLogbogApi";
import useStamdataApi from "../../hooks/useStamdataApi";
import {UddannelsesplanModel} from "../../../pages/vejledning/uddannelsesplan/uddannelsesplanModel";
import {PdfKompetenceHeaderComponent} from "./pdfHeaders/pdfKompetenceHeaderComponent";
import {PdfKompetenceHeaderModel} from "./models/pdfKompetenceHeaderModel";
import PdfRow from "./components/pdfRow";
import PdfHeaderRow from "./components/pdfHeaderRow";
import { NotatType } from "core/sharedmodels/notat/notatType";
import NoteTextViewer from "core/componentsPage/notat/tekstNotat/noteTextViewer";
import {dateTimeStringFromApiToDate} from "../../../index";
import HtmlHeading from "../titels/HtmlHeadings";
import { fetchKompetenceHeader } from "./helpers/pdfFetchers";

type UddannelsesplanerPdfParams = { brugerId: string, brugerMaalbeskrivelseId: string, forloebId: string, stillingId: string };

export const UddannelsesplanerPdf = () => {
    const { uddannelsesplanApi, logbogUserInfoApi } = useLogbogApi();
    const { stamdataMaalbeskrivelseApi, stamdataSpecialeApi, stamdataStillingApi } = useStamdataApi();

    const [uddannelsesplaner, setUddannelsesplaner] = useState<UddannelsesplanModel[]>();
    const [headerData, setHeaderData] = useState<PdfKompetenceHeaderModel>();
    const [dataLoaded, setDataLoaded] = useState(false);

    const {brugerId, brugerMaalbeskrivelseId, forloebId, stillingId} = useRouteMatch<UddannelsesplanerPdfParams>().params;
    const setError = useFetchWithErrorHandlingPdf();

    useEffect(() => {
        const fetchData = async () => {
            const [uddannelsesplanerResponse, header] = await Promise.all([
                uddannelsesplanApi.getUddannelsesplanerByForloeb(forloebId),
                fetchKompetenceHeader(brugerId, brugerMaalbeskrivelseId, { stamdataMaalbeskrivelseApi, stamdataSpecialeApi, logbogUserInfoApi, stamdataStillingApi }, {stillingId, forloebId}),
            ]);
            setUddannelsesplaner(uddannelsesplanerResponse);
            setHeaderData(header);
            setDataLoaded(true);
        };
        fetchData().catch(setError);
    }, [forloebId, brugerId, brugerMaalbeskrivelseId, stillingId]);

    return (
        <PdfComponent>
            <PdfComponent.Title title={Localizer.global_uddannelsesplaner()} heading={HtmlHeading.H3}/>

            {headerData &&
                <PdfKompetenceHeaderComponent
                    brugerId={brugerId}
                    data={headerData}
                />
            }

            <PdfComponent.BlockItem className={"card shadow-none"}>
                <PdfComponent.CardHeader>
                    <PdfHeaderRow asFragment>
                        <PdfHeaderRow.Col>{Localizer.global_title()}</PdfHeaderRow.Col>
                        <PdfHeaderRow.Col width={5}>{Localizer.global_description()}</PdfHeaderRow.Col>
                        <PdfHeaderRow.Col>{Localizer.global_klarmeldtden()}</PdfHeaderRow.Col>
                        <PdfHeaderRow.Col width={3}>{Localizer.global_godkendt()}</PdfHeaderRow.Col>
                    </PdfHeaderRow>
                </PdfComponent.CardHeader>
                {uddannelsesplaner &&
                    uddannelsesplaner.map((u, i) =>
                        <PdfComponent.NonBreakableItem>
                            <PdfComponent.CardBody>
                                <PdfRow className="flex-align-c">
                                    <PdfRow.Col>{u.titel}</PdfRow.Col>
                                    <PdfRow.Col width={5}>
                                        <NoteTextViewer
                                            id={"vejledersamtale-" + i}
                                            text={u.beskrivelse}
                                            type={NotatType.Uddannelsesplan}
                                        />
                                    </PdfRow.Col>
                                    <PdfRow.Col>{u.klarmeldtAt ? dateTimeStringFromApiToDate(u.klarmeldtAt).dateWithoutTimeFormat(true) : ""}</PdfRow.Col>
                                    <PdfRow.Col width={3}>
                                        {u.godkendelseTilkendegivetDato &&
                                            `${u.godkendelseTilkendegivetAf} d.${u.godkendelseTilkendegivetDato.dateWithoutTimeFormat(true)}`
                                        }
                                    </PdfRow.Col>
                                </PdfRow>
                            </PdfComponent.CardBody>
                        </PdfComponent.NonBreakableItem>
                    )
                }
            </PdfComponent.BlockItem>
            <PdfReadySignal when={dataLoaded} />
        </PdfComponent>
    );
}
